import { format, parseISO } from 'date-fns';
import {
  CSSProperties,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context';
import { StoryCard, StoryCardPollContent } from '../../../services';
import { pollVote } from '../../../slices';
import { useAppDispatch, useFreeLoginPopup } from '../../hooks';
import { Button, ButtonType } from '../Button';
import { RadioIndicator } from '../Radio';

import classNames from 'classnames';
import classes from './PollOptions.module.scss';

interface PollOptionsProps {
  card: StoryCard;
}

export const PollOptions: FunctionComponent<PollOptionsProps> = memo(({ card }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

  const { isAnonymous } = useContext(UserContext).userProfile;

  const groupName = useId();

  const { id: storyCardId, storyId, content } = card;

  const {
    options,
    selectedVoteIds,
    totalVotes,
    showTotalVotes,
    multipleVotes,
    showVoteResults,
    voteResults,
    allowAnonymous,
    votingTime,
  } = content as StoryCardPollContent;

  const [voteIds, setVoteIds] = useState<string[]>(selectedVoteIds);

  const hasSelectedVotes = useMemo(() => Boolean(selectedVoteIds.length), [selectedVoteIds.length]);

  useEffect(() => setVoteIds(selectedVoteIds), [selectedVoteIds]);

  const isVotingExpired = useMemo(
    () => Boolean(votingTime && votingTime < new Date().toISOString()),
    [votingTime]
  );

  const showResults = useMemo(
    () => (hasSelectedVotes || isVotingExpired) && showVoteResults,
    [hasSelectedVotes, isVotingExpired, showVoteResults]
  );

  const showMessage = useMemo(
    () => isVotingExpired || (hasSelectedVotes && !showVoteResults),
    [hasSelectedVotes, isVotingExpired, showVoteResults]
  );

  const onChange = useCallback(
    (id: string) => {
      if (hasSelectedVotes) {
        return;
      }

      setVoteIds((voteIdsPrev) => {
        if (!multipleVotes) {
          return [id];
        }

        if (voteIdsPrev.includes(id)) {
          return voteIdsPrev.filter((voteId) => voteId !== id);
        }

        return [...voteIdsPrev, id];
      });
    },
    [hasSelectedVotes, multipleVotes, setVoteIds]
  );

  const onSubmit = useCallback(() => {
    if (isAnonymous && !allowAnonymous) {
      freeLoginHandler();
      return;
    }

    dispatch(pollVote({ storyId, storyCardId, selectedVoteIds: voteIds }));
  }, [allowAnonymous, dispatch, freeLoginHandler, isAnonymous, storyCardId, storyId, voteIds]);

  return (
    <>
      <div className={classes['poll-options']}>
        {votingTime && !isVotingExpired && (
          <div className={classes['poll-options__voting-ends']}>{`${t('poll.voting-ends')} ${format(
            parseISO(votingTime),
            'dd.MM.yyyy, HH:mm'
          )}`}</div>
        )}

        <div className={classes['poll-options__total-votes']}>
          {!hasSelectedVotes &&
            !isVotingExpired &&
            `${t(`common.select-${multipleVotes ? 'few' : 'one'}`)}`}

          {showResults &&
            showTotalVotes &&
            `${totalVotes} ${t('common.votes', { count: totalVotes })}`}
        </div>

        {options.map(({ id, text }) => {
          const count = voteResults.find((voteResult) => voteResult.id === id)?.count ?? 0;
          const percentage = totalVotes ? Math.round((count / totalVotes) * 100) : 0;

          const isChecked = voteIds.includes(id);

          return (
            <label
              key={id}
              className={classNames(classes['poll-options__item-wrapper'], {
                [classes['poll-options__item-wrapper--has-votes']]: hasSelectedVotes,
                [classes['poll-options__item-wrapper--expired']]: isVotingExpired,
              })}
            >
              <input
                type={multipleVotes ? 'checkbox' : 'radio'}
                className={classes['poll-options__item-input']}
                name={groupName}
                checked={isChecked}
                disabled={hasSelectedVotes || isVotingExpired}
                onChange={() => onChange(id)}
              />

              <div
                className={classNames(classes['poll-options__item'], {
                  [classes['poll-options__item--result']]: showResults,
                })}
                {...(showResults && {
                  style: { '--result-width': `${percentage}%` } as CSSProperties,
                })}
              >
                <RadioIndicator
                  className={classNames(classes['poll-options__item-radio'], {
                    [classes['poll-options__item-radio--has-votes']]: hasSelectedVotes,
                  })}
                  checked={isChecked}
                  label={text}
                />
                {showResults && showTotalVotes && (
                  <div className={classes['poll-options__item-count']}>
                    {count} {t('common.votes', { count })}
                  </div>
                )}
              </div>

              {showResults && (
                <div className={classes['poll-options__percentage']}>{percentage}%</div>
              )}
            </label>
          );
        })}

        {!isVotingExpired && !hasSelectedVotes && (
          <Button
            label={t('common.submit')}
            type={ButtonType.primary}
            disabled={!Boolean(voteIds.length)}
            onClick={onSubmit}
            fullWidth
          />
        )}

        {showMessage && (
          <div className={classes['poll-options__message']}>
            {t(`poll.${isVotingExpired ? 'expire' : 'vote'}-message`)}
          </div>
        )}
      </div>

      {freeLoginModal}
    </>
  );
});
